<template>
  <Tabs
    :tabs='availableJsonClasses'
    :with-router='false'
    :default-tab='activeJsonClass'
    @set-active-tab='setActiveJsonClass'
  />
  <div v-if='!editMode'>
    <div
      v-for='(values, key) in parsedOptions'
      :key='key'
      class='mt-4 text-sm'
    >
      <div
        v-if='values'
        class='flex flex-row items-center gap-4 text-sm'
      >
        <span class='rounded-lg bg-slate-700 px-3 py-1 font-semibold text-white'>{{ key }}</span>
        <span class=''>=</span>
        <span>
          {{ typeof values === 'object' ? JSON.stringify(values) : values }}
        </span>
      </div>
    </div>
  </div>
  <div
    v-else
    class='mt-4'
  >
    <div class='grid grid-cols-2 gap-x-20 gap-y-2'>
      <CodeField
        v-if='jsonEditorOptions.length'
        v-model='jsonEditorOptions'
        :edit-mode='editMode'
        :model-value='jsonEditorOptions'
        lang='json'
        mode='json'
        style='height: 100%; min-height: 300px;'
        :name='fieldKey'
        :field='{ mandatory: false, type: "json"}'
        @update:model-value='updateValue(availableJsonClasses)'
      />
      <div>
        <div
          v-for='(value, key) in jsonEditorLegend'
          :key='key'
        >
          <div class='flex flex-row text-sm'>
            <span class='w-1/6 font-bold'>{{ key }}</span>
            <ArrowNarrowRightIcon class='mx-2 h-5 w-5'/>
            <span>{{ value }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { computed, defineComponent, onMounted, ref, watch } from 'vue'
  import Tabs from '@/components/partials/Tabs.vue'
  import { useProductStore } from '@/store/product'
  import CodeField from '@/components/inputs/code-field/CodeField.vue'
  import useInputFieldBase, { InputFieldEmits, InputFieldProps } from '@/compositions/useInputFieldBase'
  import { ArrowNarrowRightIcon } from '@heroicons/vue/outline'
  import { isInteger } from 'lodash-es'

  export default defineComponent({
    name: 'MultiJsonField',
    components: {
      CodeField,
      Tabs,
      ArrowNarrowRightIcon,
    },
    props: {
      ...InputFieldProps,
      fieldKey: {
        type: String,
        default: 'rules',
      },
      context: {
        type: String,
        default: undefined,
      },
    },
    emits: [
      ...InputFieldEmits,
      'update-record',
    ],
    setup(props, { emit }) {
      const { updateValue } = useInputFieldBase()
      const productStore = useProductStore()
      const availableJsonClasses = ref([])
      const activeJsonClass = ref('all')
      const jsonEditorOptions = ref([])
      const jsonEditorLegend = ref([])
      const currentOptionsFromProp = ref(props.modelValue ?? [])

      onMounted(() => {
        productStore.loadJsonClasses(props.context).then(() => {
          availableJsonClasses.value = JSON.parse(JSON.stringify(productStore.availableJsonClassesFor(props.context)))

          availableJsonClasses.value.unshift({
            key: 'all',
            value: 'all',
            name: 'all',
            label: 'All',
            active: true,
            options: [],
            legend: [],
          })

          setupVariables()
        })
      })

      const setActiveJsonClass = (jsonClass: string) => {
        activeJsonClass.value = jsonClass
      }

      // Two record shapes exist in the wild:
      // - crediting_rules: [{ key: 'trade_volume_multiplier', options: {...} }]
      // - condition_rules: [{ deposit: {...} }, { field: { promocode: {...} } }]
      const findRuleOptions = (list: Array<Record<string, unknown>>, ruleKey: string): unknown => {
        for (const value of list) {
          if (!value) continue

          if ('key' in value) {
            if (value.key === ruleKey) return value.options
          } else if (value[ruleKey] !== undefined) {
            return value[ruleKey]
          }
        }

        return undefined
      }

      const setupVariables = () => {
        availableJsonClasses.value = availableJsonClasses.value.filter((item, key) => isInteger(key))

        if (activeJsonClass.value === 'all') {
          const combined: Record<string, unknown> = {
          }

          availableJsonClasses.value.forEach((item: object) => {
            if (item.key !== 'all') {
              const ruleOptions = findRuleOptions(currentOptionsFromProp.value, item.key)

              combined[item.key] = ruleOptions ?? item.options
            }
          })
          jsonEditorOptions.value = JSON.stringify(combined)
          jsonEditorLegend.value = {
          }

          return
        }

        const jsonEditor = availableJsonClasses.value.find((item: object) => item.key === activeJsonClass.value)

        const ruleOptions = findRuleOptions(currentOptionsFromProp.value, activeJsonClass.value)

        jsonEditorOptions.value = JSON.stringify(ruleOptions ?? jsonEditor.options)
        jsonEditorLegend.value = jsonEditor?.legend
      }

      watch(activeJsonClass, (newValue, oldValue) => {
        if (newValue !== oldValue) {
          setupVariables()
        }
      })

      watch(jsonEditorOptions, (newValue) => {
        if (!newValue) return

        if (activeJsonClass.value === 'all') {
          try {
            const parsed = JSON.parse(newValue)

            availableJsonClasses.value.forEach(available => {
              if (available.key !== 'all' && parsed[available.key] !== undefined) {
                available.options = parsed[available.key]
              }
            })
          } catch {}
        } else {
          availableJsonClasses.value.forEach(available => {
            if (available.key === activeJsonClass.value) {
              available.options = JSON.parse(newValue)
            }
          })
        }

        const exportable = availableJsonClasses.value.filter((item, key) => isInteger(key) && item.key !== 'all')

        emit('update-record', {
          key: props.fieldKey, value: exportable,
        })
      }, {
        deep: true,
      })

      const parsedOptions = computed(() => {
        let options = {
        }

        try {
          options = JSON.parse(jsonEditorOptions.value)
        } catch (e) {

        }

        return options
      })

      return {
        availableJsonClasses,
        activeJsonClass,
        jsonEditorOptions,
        jsonEditorLegend,
        currentOptionsFromProp,
        setActiveJsonClass,
        updateValue,
        parsedOptions,
      }
    },
  })
</script>
